import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Collapse, Typography } from '@material-ui/core'
import { ExpandLess, ExpandMore } from '@material-ui/icons'

import Tooltip from 'components/Tooltip'
import SellerTransferList from 'components/GoalWizard/SellerTransferList'
import { IGoal, IUserWorking } from 'store/modules/goal/types'
import { BulkGoalClasses, StoreGoalConfig } from '../../types'
import { getUsersAccWeight, hasValidUsersSelection } from '../../utils'

const INITIAL_VISIBLE_SELLERS = 12
const SELLER_RENDER_BATCH_SIZE = 12

interface GoalStepProps {
  addSellerToGoal: (storeId: string, user: IUserWorking) => void
  classes: BulkGoalClasses
  removeSellerFromGoal: (storeId: string, user: IUserWorking) => void
  storeGoalConfigs: StoreGoalConfig[]
  unlockSellerWeights: (storeId: string) => void
  updateSellerDsr: (storeId: string, user: IUserWorking, dsr: boolean) => void
  updateSellerWeight: (storeId: string, user: IUserWorking, type: 'auto' | 'fixed', weight: number) => void
}

interface StoreGoalAccordionProps extends Omit<GoalStepProps, 'storeGoalConfigs'> {
  config: StoreGoalConfig
  isExpanded: boolean
  onToggle: (storeId: string) => void
}

const StoreGoalAccordion = React.memo(({
  addSellerToGoal,
  classes,
  config,
  isExpanded,
  onToggle,
  removeSellerFromGoal,
  unlockSellerWeights,
  updateSellerDsr,
  updateSellerWeight
}: StoreGoalAccordionProps) => {
  const [visibleNotWorkingCount, setVisibleNotWorkingCount] = useState(INITIAL_VISIBLE_SELLERS)
  const [visibleWorkingCount, setVisibleWorkingCount] = useState(INITIAL_VISIBLE_SELLERS)
  const visibleNotWorkingCountRef = useRef(INITIAL_VISIBLE_SELLERS)
  const visibleWorkingCountRef = useRef(INITIAL_VISIBLE_SELLERS)
  const selectedSellersCount = config.usersWorking.length
  const totalSellersCount = selectedSellersCount + config.usersNotWorking.length
  const usersAccWeight = getUsersAccWeight(config.usersWorking)
  const expectedWeight = selectedSellersCount * 100
  const hasWeightError = Boolean(config.distribute) && usersAccWeight.toFixed(0) !== expectedWeight.toFixed(0)
  const hasMonthGoals = config.monthGoalTemplates.length > 0
  const isValid = hasMonthGoals && hasValidUsersSelection(config)
  const hasLockedWeights = config.usersWorking.some(user => user.weightLocked)
  const goalData = {
    distribute: Boolean(config.distribute),
    dsr: Number(config.dsr || 0)
  } as IGoal
  const statusLabel = !hasMonthGoals
    ? 'Sem metas anteriores'
    : selectedSellersCount === 0
      ? 'Sem vendedores'
      : hasWeightError ? 'Ajustar pesos' : 'Completo'
  const correctionMessage = !hasMonthGoals
    ? 'Não há metas individuais anteriores para calcular automaticamente.'
    : selectedSellersCount === 0
      ? 'Selecione pelo menos um vendedor para participar da meta.'
      : hasWeightError
        ? `Ajuste os pesos para totalizar ${expectedWeight.toFixed(0)}%`
        : 'Seleção de vendedores completa.'
  const statusTooltipId = `bulk-seller-status-${config.storeId}`

  useEffect(() => {
    if (!isExpanded) {
      visibleNotWorkingCountRef.current = INITIAL_VISIBLE_SELLERS
      visibleWorkingCountRef.current = INITIAL_VISIBLE_SELLERS
      setVisibleNotWorkingCount(INITIAL_VISIBLE_SELLERS)
      setVisibleWorkingCount(INITIAL_VISIBLE_SELLERS)
      return
    }

    let timeout = 0
    let nextNotWorkingCount = visibleNotWorkingCountRef.current
    let nextWorkingCount = visibleWorkingCountRef.current
    const renderNextBatch = () => {
      nextNotWorkingCount = Math.min(config.usersNotWorking.length, nextNotWorkingCount + SELLER_RENDER_BATCH_SIZE)
      nextWorkingCount = Math.min(config.usersWorking.length, nextWorkingCount + SELLER_RENDER_BATCH_SIZE)

      visibleNotWorkingCountRef.current = nextNotWorkingCount
      visibleWorkingCountRef.current = nextWorkingCount
      setVisibleNotWorkingCount(nextNotWorkingCount)
      setVisibleWorkingCount(nextWorkingCount)

      const hasMoreNotWorking = nextNotWorkingCount < config.usersNotWorking.length
      const hasMoreWorking = nextWorkingCount < config.usersWorking.length
      if (hasMoreNotWorking || hasMoreWorking) {
        timeout = window.setTimeout(renderNextBatch, 16)
      }
    }

    timeout = window.setTimeout(renderNextBatch, 16)
    return () => window.clearTimeout(timeout)
  }, [config.usersNotWorking.length, config.usersWorking.length, isExpanded])

  return (
    <div className={classes.sellerAccordionItem}>
      <button
        className={classes.sellerAccordionHeader}
        type='button'
        onClick={() => onToggle(config.storeId)}
      >
        <div className={classes.sellerAccordionTitle}>
          <strong>{config.storeName}</strong>
          <span>{selectedSellersCount} de {totalSellersCount} vendedores selecionados</span>
        </div>
        <div className={classes.sellerAccordionSummary}>
          <Tooltip fixed place='top' id={statusTooltipId} content={<p>{correctionMessage}</p>} />
          <span data-tip data-for={statusTooltipId} className={isValid ? classes.sellerStatusOk : classes.sellerStatusWarning}>{statusLabel}</span>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </div>
      </button>

      <Collapse in={isExpanded} timeout={180} unmountOnExit>
        <div className={classes.sellerAccordionContent}>
          <SellerTransferList
            data={goalData}
            expectedWeight={expectedWeight}
            hasLockedWeights={hasLockedWeights}
            hasWeightError={hasWeightError}
            layout='bulk'
            unlockTooltipId={`bulk-unlock-weight-${config.storeId}`}
            usersAccWeight={usersAccWeight}
            usersNotWorking={config.usersNotWorking}
            usersWorking={config.usersWorking}
            visibleNotWorkingCount={visibleNotWorkingCount}
            visibleWorkingCount={visibleWorkingCount}
            weightTooltipId={`bulk-total-weight-${config.storeId}`}
            onAdd={user => addSellerToGoal(config.storeId, user)}
            onRemove={user => removeSellerFromGoal(config.storeId, user)}
            onUnlockWeights={() => unlockSellerWeights(config.storeId)}
            onUpdateDsr={(user, dsr) => updateSellerDsr(config.storeId, user, dsr)}
            onUpdateWeight={(user, type, weight) => updateSellerWeight(config.storeId, user, type, weight)}
          />
        </div>
      </Collapse>
    </div>
  )
})

StoreGoalAccordion.displayName = 'StoreGoalAccordion'

const GoalStep = ({
  addSellerToGoal,
  classes,
  removeSellerFromGoal,
  storeGoalConfigs,
  unlockSellerWeights,
  updateSellerDsr,
  updateSellerWeight
}: GoalStepProps) => {
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(storeGoalConfigs[0]?.storeId || null)

  const handleToggleAccordion = useCallback((storeId: string) => {
    setExpandedStoreId(currentStoreId => currentStoreId === storeId ? null : storeId)
  }, [])

  return (
    <div className={classes.stepContainer}>
      <h1 className={classes.title}>Selecione quem irá participar das metas</h1>
      <Typography className={classes.subtitle}>
        Por padrão, são selecionados os vendedores que participavam da última meta de cada loja. Você pode remover vendedores, alterar DSR e ajustar pesos antes da criação das metas individuais.
      </Typography>

      <div className={classes.sellerAccordionList}>
        {storeGoalConfigs.map(config => (
          <StoreGoalAccordion
            addSellerToGoal={addSellerToGoal}
            classes={classes}
            config={config}
            isExpanded={expandedStoreId === config.storeId}
            key={config.storeId}
            onToggle={handleToggleAccordion}
            removeSellerFromGoal={removeSellerFromGoal}
            unlockSellerWeights={unlockSellerWeights}
            updateSellerDsr={updateSellerDsr}
            updateSellerWeight={updateSellerWeight}
          />
        ))}
      </div>
    </div>
  )
}

export default GoalStep
